import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import utilitiesAPI from '../api/utilitiesAPI';
import { useUtilitiesCompany } from '../context/UtilitiesCompanyContext';
import UtilitiesShell, { UtilitiesBanner } from './UtilitiesShell';
import { RESOURCE_LIST, resourceAllowedForCompany } from '../utils/utilitiesResources';
import { formatMoney, isUnauthorized } from '../utils/utilitiesFormat';

const FORM_LABELS = Object.fromEntries(RESOURCE_LIST.map((r) => [r.key, r.label]));

/** Turns the dashboard response into one tile per form that has counts. */
function formTiles(data) {
  const source = data?.forms || data || {};
  return Object.entries(source)
    .filter(([, v]) => v && typeof v === 'object' && ('draft' in v || 'posted' in v))
    .map(([key, v]) => ({
      key,
      label: FORM_LABELS[key] || key.replace(/[-_]/g, ' '),
      draft: Number(v.draft) || 0,
      posted: Number(v.posted) || 0,
    }));
}

export default function UtilitiesDashboardPage() {
  const navigate = useNavigate();
  const { company, companyId } = useUtilitiesCompany();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!companyId) return undefined;
    let cancelled = false;
    setLoading(true);
    setError(null);
    utilitiesAPI
      .dashboard(companyId)
      .then((res) => !cancelled && setData(res))
      .catch((err) => {
        if (cancelled) return;
        if (isUnauthorized(err)) return navigate('/bu-dashboard/login', { replace: true });
        setError(err.message);
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [companyId, navigate]);

  const tiles = formTiles(data).filter((t) => {
    const res = RESOURCE_LIST.find((r) => r.key === t.key);
    return !res || resourceAllowedForCompany(res, company);
  });
  const outstanding =
    data?.outstanding_vendor_bills ?? data?.outstanding_bills ?? data?.outstanding ?? null;

  return (
    <UtilitiesShell
      title="Utilities Dashboard"
      subtitle={company ? `Working in ${company.name}` : 'Select a company to continue'}
    >
      {error && <UtilitiesBanner>{error}</UtilitiesBanner>}
      {loading && <p className="text-sm text-slate-500">Loading…</p>}

      {!loading && tiles.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {tiles.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => navigate(`/bu-dashboard/utilities/${t.key}`)}
              className="text-left rounded-xl border border-slate-200 bg-white p-5 hover:shadow-sm transition"
            >
              <p className="text-sm font-medium text-slate-700">{t.label}</p>
              <div className="mt-3 flex gap-6">
                <div>
                  <p className="text-2xl font-semibold text-amber-700">{t.draft}</p>
                  <p className="text-xs text-slate-500">Draft</p>
                </div>
                <div>
                  <p className="text-2xl font-semibold text-green-700">{t.posted}</p>
                  <p className="text-xs text-slate-500">Posted</p>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {!loading && outstanding !== null && (
        <div className="mt-6 rounded-xl border border-slate-200 bg-white p-5 max-w-sm">
          <p className="text-sm text-slate-500">Outstanding vendor bills</p>
          <p className="text-2xl font-semibold text-slate-900 mt-1">
            {typeof outstanding === 'object' ? formatMoney(outstanding.amount) : formatMoney(outstanding)}
          </p>
        </div>
      )}

      {!loading && !error && tiles.length === 0 && data && (
        <p className="text-sm text-slate-500">No form activity to show for this company yet.</p>
      )}
    </UtilitiesShell>
  );
}
